import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { Grid, Header, Container } from 'semantic-ui-react';
import { useLocation, useHistory } from 'react-router-dom';
import './styles.scss';

import BidTable from './BidTable';
import BidFilters from './BidFilters';
import { BidFiltersType } from '~/types/bid/BidFiltersType';
import '~/features/admin/companies/styles.scss';

const BID_FILTERS_DEBOUNCE_MS = 400;

function truncateForMeta(s: string, max = 56): string {
  if (s.length <= max) {
    return s;
  }
  return `${s.slice(0, max - 1)}...`;
}

const EMPTY_BID_FILTERS: BidFiltersType = {
  selectedStatuses: [],
  amountMin: '',
  amountMax: '',
  selectedUnits: [],
  selectedDeploymentType: '',
  projectName: '',
  portfolioName: '',
  investorCompanyName: '',
  state: '',
  dateFrom: '',
  dateTo: '',
};

export default function BidSearch() {
  const location = useLocation();
  const history = useHistory();
  
  // Parse URL search params
  const getSearchParams = () => {
    return new URLSearchParams(location.search);
  };
  
  // Initialize filters from URL params
  const getInitialFilters = (): BidFiltersType => {
    const params = getSearchParams();
    return {
      selectedStatuses: params.get('statuses')?.split(',').filter(Boolean) || [],
      amountMin: params.get('amountMin') || '',
      amountMax: params.get('amountMax') || '',
      selectedUnits: params.get('units')?.split(',').filter(Boolean) || [],
      selectedDeploymentType: params.get('revenueType') || '',
      projectName: params.get('projectName') || '',
      portfolioName: params.get('portfolioName') || '',
      investorCompanyName: params.get('investorCompanyName') || '',
      state: params.get('state') || '',
      dateFrom: params.get('dateFrom') || '',
      dateTo: params.get('dateTo') || ''
    };
  };
  
  const [filters, setFilters] = useState<BidFiltersType>(getInitialFilters());
  const [debouncedFilters, setDebouncedFilters] = useState<BidFiltersType>(getInitialFilters());
  const [currentPage, setCurrentPage] = useState<number>(
    parseInt(getSearchParams().get('page') || '1', 10)
  );

  ReactGA.send({ hitType: 'pageview', page: '/admin/bids', title: 'All Bids' });

  useEffect(() => {
    document.title = 'All Bids | Conductor Solar';
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedFilters(filters);
    }, BID_FILTERS_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [filters]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (debouncedFilters.selectedStatuses.length > 0) {
      params.set('statuses', debouncedFilters.selectedStatuses.join(','));
    }
    if (debouncedFilters.amountMin) {
      params.set('amountMin', debouncedFilters.amountMin);
    }
    if (debouncedFilters.amountMax) {
      params.set('amountMax', debouncedFilters.amountMax);
    }
    if (debouncedFilters.selectedUnits.length > 0) {
      params.set('units', debouncedFilters.selectedUnits.join(','));
    }
    if (debouncedFilters.selectedDeploymentType) {
      params.set('revenueType', debouncedFilters.selectedDeploymentType);
    }
    if (debouncedFilters.projectName) {
      params.set('projectName', debouncedFilters.projectName);
    }
    if (debouncedFilters.portfolioName) {
      params.set('portfolioName', debouncedFilters.portfolioName);
    }
    if (debouncedFilters.investorCompanyName) {
      params.set('investorCompanyName', debouncedFilters.investorCompanyName);
    }
    if (debouncedFilters.state) {
      params.set('state', debouncedFilters.state);
    }
    if (debouncedFilters.dateFrom) {
      params.set('dateFrom', debouncedFilters.dateFrom);
    }
    if (debouncedFilters.dateTo) {
      params.set('dateTo', debouncedFilters.dateTo);
    }
    params.set('page', currentPage.toString());

    history.replace(`${location.pathname}?${params.toString()}`);
  }, [currentPage, debouncedFilters, history, location.pathname]);

  const handleFiltersChange = (newFilters: BidFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    
    // Update URL with new page while preserving other params
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    history.push(`${location.pathname}?${params.toString()}`);
  };

  const clearAllFilters = () => {
    setFilters(EMPTY_BID_FILTERS);
    setCurrentPage(1);
  };

  const hasFilters = useMemo(() => {
    return (
      filters.selectedStatuses.length > 0 ||
      filters.amountMin.length > 0 ||
      filters.amountMax.length > 0 ||
      filters.selectedUnits.length > 0 ||
      filters.selectedDeploymentType.length > 0 ||
      filters.projectName.length > 0 ||
      filters.portfolioName.length > 0 ||
      filters.investorCompanyName.length > 0 ||
      filters.state.length > 0 ||
      filters.dateFrom.length > 0 ||
      filters.dateTo.length > 0
    );
  }, [filters]);

  const activeFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (filters.projectName) {
      parts.push(`Project contains "${truncateForMeta(filters.projectName)}"`);
    }
    if (filters.portfolioName) {
      parts.push(`Portfolio contains "${truncateForMeta(filters.portfolioName)}"`);
    }
    if (filters.investorCompanyName) {
      parts.push(`Investor contains "${truncateForMeta(filters.investorCompanyName)}"`);
    }
    if (filters.selectedUnits.length > 0) {
      parts.push(`Unit: ${[...filters.selectedUnits].sort().join(', ')}`);
    }
    if (filters.selectedDeploymentType) {
      parts.push(`Revenue type: ${filters.selectedDeploymentType}`);
    }
    if (filters.state) {
      parts.push(`State: ${filters.state}`);
    }
    if (filters.selectedStatuses.length > 0) {
      parts.push(`Status: ${[...filters.selectedStatuses].sort().join(', ')}`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [filters]);

  return (
    <div className="project-search-container bid-search-container">
      <Container fluid>
        <Grid centered>
          <Grid.Column mobile={12} tablet={12} computer={12} largeScreen={12}>
            <Header as="h1">All Bids</Header>

            <div className="card">
              <BidFilters filters={filters} onFiltersChange={handleFiltersChange} />
            </div>
            <div className="card">
              <BidTable 
                bidFilters={debouncedFilters}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                hasFilters={hasFilters}
                activeFilterDescription={activeFilterDescription}
                onClearFilters={clearAllFilters}
              />
            </div>
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
}
